import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { PoolSort, VolumePeriod, YieldPeriod } from "../types";
import {
  activeFilterCount,
  columnOptions,
  defaultCatalogFilters,
  normalizeCatalogFilters,
  type CatalogColumn,
  type CatalogFilters,
} from "../lib/catalog-preferences";

type Menu = "filters" | "columns" | null;
const volumePeriods = ["5m", "15m", "30m", "1h", "2h", "4h", "8h", "24h", "7d"] as const;
export function CatalogControls(props: {
  filters: CatalogFilters;
  columns: Record<CatalogColumn, boolean>;
  sort: PoolSort;
  direction: "asc" | "desc";
  volumePeriod: VolumePeriod;
  yieldPeriod: YieldPeriod;
  onApplyFilters: (filters: CatalogFilters) => void;
  onColumnsChange: (columns: Record<CatalogColumn, boolean>) => void;
  onResetColumns: () => void;
  onSortChange: (sort: PoolSort) => void;
  onDirectionChange: () => void;
  onVolumePeriodChange: (period: VolumePeriod) => void;
  onYieldPeriodChange: (period: YieldPeriod) => void;
}) {
  const [menu, setMenu] = useState<Menu>(null);
  const [draft, setDraft] = useState(props.filters);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [placement, setPlacement] = useState<CSSProperties>({ visibility: "hidden" });
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const filterTrigger = useRef<HTMLButtonElement>(null);
  const columnsTrigger = useRef<HTMLButtonElement>(null);
  const openedBy = useRef<HTMLButtonElement | null>(null);
  const focusOnOpen = useRef(false);

  const close = useCallback((restoreFocus = true) => {
    const trigger = openedBy.current;
    setMenu(null);
    if (restoreFocus) window.setTimeout(() => trigger?.focus({ preventScroll: true }));
  }, []);

  function toggle(next: Exclude<Menu, null>, trigger: HTMLButtonElement) {
    if (menu === next) {
      close();
      return;
    }
    openedBy.current = trigger;
    focusOnOpen.current = true;
    if (next === "filters") {
      setDraft(props.filters);
      setFilterError(null);
    }
    setPlacement({ visibility: "hidden" });
    setMenu(next);
  }

  useLayoutEffect(() => {
    if (!menu) return;
    const place = () => {
      const trigger = menu === "filters" ? filterTrigger.current : columnsTrigger.current;
      const popup = panel.current;
      if (!trigger || !popup) return;
      const gutter = 8;
      const anchor = trigger.getBoundingClientRect();
      const width = Math.min(menu === "filters" ? 400 : 300, window.innerWidth - gutter * 2);
      const maxHeight = window.innerHeight - gutter * 2;
      // Measure wrapping and borders at the final constrained size before positioning.
      popup.style.width = `${String(width)}px`;
      popup.style.maxHeight = `${String(maxHeight)}px`;
      const height = popup.getBoundingClientRect().height;
      const next = {
        left: Math.min(Math.max(gutter, anchor.right - width), window.innerWidth - width - gutter),
        top: Math.max(
          gutter,
          Math.min(anchor.bottom + gutter, window.innerHeight - height - gutter),
        ),
        width,
        maxHeight,
      };
      setPlacement((current) =>
        current.visibility !== "hidden" &&
        current.left === next.left &&
        current.top === next.top &&
        current.width === width &&
        current.maxHeight === maxHeight
          ? current
          : next,
      );
    };
    place();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (panel.current) observer?.observe(panel.current, { box: "border-box" });
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [menu]);

  useLayoutEffect(() => {
    if (menu && placement.visibility !== "hidden" && focusOnOpen.current) {
      focusOnOpen.current = false;
      closeButton.current?.focus({ preventScroll: true });
    }
  }, [menu, placement]);

  useEffect(() => {
    if (!menu) return;
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) close(false);
    };
    window.addEventListener("keydown", key);
    window.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("keydown", key);
      window.removeEventListener("pointerdown", outside);
    };
  }, [close, menu]);

  const toggleRow = (
    checked: boolean,
    label: string,
    onChange: (checked: boolean) => void,
    key?: string,
    appearance: "switch" | "check" = "switch",
    ariaLabel?: string,
  ) => (
    <label className={`catalog-toggle ${appearance}`} key={key}>
      <span>{label}</span>
      <input
        type="checkbox"
        aria-label={ariaLabel}
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <span
        className={appearance === "switch" ? "catalog-switch" : "catalog-check"}
        aria-hidden="true"
      >
        {appearance === "check" && (
          <svg viewBox="0 0 12 12">
            <path d="m2.5 6.2 2.3 2.3 4.7-4.8" />
          </svg>
        )}
      </span>
    </label>
  );

  const popupHeader = (title: string, resetLabel: string, onReset: () => void) => (
    <div className="catalog-popup-heading">
      <h2>{title}</h2>
      <button ref={closeButton} type="button" onClick={onReset} aria-label={resetLabel}>
        Reset
      </button>
    </div>
  );

  return (
    <div className="catalog-controls" ref={root}>
      <button
        ref={filterTrigger}
        type="button"
        className="button ghost catalog-trigger"
        aria-label="Catalog filters"
        aria-haspopup="dialog"
        aria-expanded={menu === "filters"}
        onClick={(event) => {
          toggle("filters", event.currentTarget);
        }}
      >
        <svg className="catalog-control-icon" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M2 3h12L9.5 8v4l-3 1V8L2 3Z" />
        </svg>
        Filters
        {activeFilterCount(props.filters) > 0 && <b>{activeFilterCount(props.filters)}</b>}
      </button>
      <button
        ref={columnsTrigger}
        type="button"
        className="button ghost catalog-trigger"
        aria-label="Catalog columns"
        aria-haspopup="dialog"
        aria-expanded={menu === "columns"}
        onClick={(event) => {
          toggle("columns", event.currentTarget);
        }}
      >
        <svg className="catalog-control-icon columns" viewBox="0 0 16 16" aria-hidden="true">
          <rect x="2.5" y="3" width="11" height="10" rx="1.5" />
          <path d="M6.2 3v10M9.8 3v10" />
        </svg>
        Columns
      </button>

      {menu === "filters" && (
        <div
          ref={panel}
          style={placement}
          className="catalog-popup filter-popup"
          role="dialog"
          aria-label="Catalog filters"
        >
          {popupHeader("Filters", "Reset filters", () => {
            setDraft(defaultCatalogFilters);
            setFilterError(null);
          })}
          <div className="catalog-filter-fields">
            <label className="catalog-field-row">
              <span>Min TVL, USD</span>
              <input
                aria-label="Minimum TVL (USD)"
                inputMode="decimal"
                placeholder="Any"
                value={draft.minTvl}
                onChange={(event) => {
                  setDraft({ ...draft, minTvl: event.target.value });
                }}
              />
            </label>
            <label className="catalog-field-row">
              <span>Min 24H volume, USD</span>
              <input
                aria-label="Minimum 24h volume (USD)"
                inputMode="decimal"
                placeholder="Any"
                value={draft.minVolume}
                onChange={(event) => {
                  setDraft({ ...draft, minVolume: event.target.value });
                }}
              />
            </label>
          </div>
          <div className="catalog-toggle-list">
            {toggleRow(draft.noWarnings, "No warnings", (checked) => {
              setDraft({ ...draft, noWarnings: checked });
            })}
            {toggleRow(
              draft.hasRewards,
              "Has rewards",
              (checked) => {
                setDraft({ ...draft, hasRewards: checked });
              },
              undefined,
              "switch",
              "Rewards",
            )}
            {toggleRow(
              draft.withoutMemes,
              "Without memes",
              (checked) => {
                setDraft({ ...draft, withoutMemes: checked });
              },
              undefined,
              "switch",
              "Canonical mints only",
            )}
            {toggleRow(
              draft.hasAdaptiveFee,
              "Adaptive fee",
              (checked) => {
                setDraft({ ...draft, hasAdaptiveFee: checked });
              },
              undefined,
              "switch",
              "Adaptive fees",
            )}
            {toggleRow(draft.isWavebreak, "Wavebreak", (checked) => {
              setDraft({ ...draft, isWavebreak: checked });
            })}
            {toggleRow(
              draft.hasLockedLiquidity,
              `Locked liquidity ≥ ${draft.minLockedLiquidityPercent || "1"}%`,
              (checked) => {
                setDraft({ ...draft, hasLockedLiquidity: checked });
              },
              undefined,
              "switch",
              "Locked liquidity",
            )}
          </div>
          {draft.hasLockedLiquidity && (
            <label className="locked-percentage">
              <span>Minimum locked liquidity</span>
              <span className="catalog-percent">
                <input
                  aria-label="Minimum locked liquidity percentage"
                  inputMode="decimal"
                  value={draft.minLockedLiquidityPercent}
                  onChange={(event) => {
                    setDraft({ ...draft, minLockedLiquidityPercent: event.target.value });
                  }}
                />
                <span aria-hidden="true">%</span>
              </span>
              <small>Greater than 0% and at most 100%. Default 1%.</small>
            </label>
          )}
          {filterError && (
            <p className="form-error" role="alert">
              {filterError}
            </p>
          )}
          <div className="catalog-popup-actions">
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                close();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button primary"
              aria-label="Apply filters"
              onClick={() => {
                try {
                  props.onApplyFilters(normalizeCatalogFilters(draft));
                  close();
                } catch (reason) {
                  setFilterError(reason instanceof Error ? reason.message : "Invalid filters");
                }
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {menu === "columns" && (
        <div
          ref={panel}
          style={placement}
          className="catalog-popup columns-popup"
          role="dialog"
          aria-label="Catalog columns"
        >
          {popupHeader("Columns", "Reset columns", props.onResetColumns)}
          <div className="catalog-toggle-list">
            {columnOptions.map((option) => (
              <div key={option.value} className="catalog-column-option">
                {toggleRow(
                  props.columns[option.value],
                  option.label,
                  (checked) => {
                    props.onColumnsChange({ ...props.columns, [option.value]: checked });
                  },
                  option.value,
                  "check",
                )}
                {option.value === "volume" && (
                  <fieldset className="catalog-periods">
                    <legend>Volume period</legend>
                    <div>
                      {volumePeriods.map((period) => (
                        <button
                          key={period}
                          type="button"
                          className={props.volumePeriod === period ? "active" : ""}
                          aria-pressed={props.volumePeriod === period}
                          onClick={() => {
                            props.onVolumePeriodChange(period);
                          }}
                        >
                          {period.endsWith("m") ? period : period.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                )}
              </div>
            ))}
          </div>
          <div className="catalog-settings">
            <label>
              <span>Sort by</span>
              <select
                value={props.sort}
                onChange={(event) => {
                  props.onSortChange(event.target.value as PoolSort);
                }}
              >
                {columnOptions
                  .filter((option) => option.sort)
                  .map((option) => (
                    <option value={option.sort} key={option.sort}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </label>
            <button type="button" className="button ghost" onClick={props.onDirectionChange}>
              {props.direction === "desc" ? "Descending" : "Ascending"}
            </button>
          </div>
          <div className="catalog-popup-actions">
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                close();
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
